import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { AgreementProductReadModel, PermissionKeys, useGetProductsByIdChildrenQuery } from '@client/api';
import {
  isDefinedString,
  TableCell,
  TableRow,
  Text,
  toMoneyStringOrUndefined,
  toPriceString,
  useAreaMetricString,
} from '@client/toolkit';

type AgreementCommonCostItemProps = {
  agreementProduct?: AgreementProductReadModel;
  totalArea?: number;
  isFetching?: boolean;
  expanded?: boolean;
  onToggleExpanded?: () => void;
  canEdit?: boolean;
};

export function AgreementCommonCostItem({
  agreementProduct,
  totalArea,
  isFetching,
  expanded = false,
  onToggleExpanded,
  canEdit = true,
}: AgreementCommonCostItemProps) {
  const { t: tGlobal } = useTranslation('global');

  const productId = agreementProduct?.price?.product?.id;
  const isFelleskostnader = agreementProduct?.price?.product?.category === 'Felleskostnader';

  const { data: subProducts } = useGetProductsByIdChildrenQuery(
    { id: productId ?? '' },
    { skip: !productId || !isFelleskostnader || !canEdit },
  );

  const hasSubProducts = (subProducts?.length ?? 0) > 0;
  const persistedChildren = agreementProduct?.children ?? [];
  // Area from Utleieobjekt (passed from parent)
  const areaValue = useMemo(() => {
    if (totalArea == null || totalArea <= 0) return undefined;
    return { metric: totalArea, imperial: 0 };
  }, [totalArea]);
  const areaString = useAreaMetricString(areaValue);

  // Override area - now stored in overrideArea field, not quantity
  const overrideAreaValue = useMemo(() => {
    const overrideArea = agreementProduct?.overrideArea;
    if (overrideArea == null) return undefined;
    return { metric: overrideArea, imperial: 0 };
  }, [agreementProduct?.overrideArea]);
  const overrideAreaString = useAreaMetricString(overrideAreaValue);

  const productName = agreementProduct?.price?.product?.name;
  const productPrice = toPriceString(agreementProduct?.price);
  const overwrittenPrice = agreementProduct?.overwrittenUnitAmount
    ? toMoneyStringOrUndefined(agreementProduct.overwrittenUnitAmount)
    : undefined;

  // Calculate total amount correctly using effective area and effective price
  const totalAmountString = useMemo(() => {
    // Effective area: use overrideArea if set, otherwise use totalArea from spaces
    const effectiveArea = agreementProduct?.overrideArea ?? totalArea ?? 0;
    if (effectiveArea <= 0) return undefined;

    // Effective price: use overwrittenUnitAmount if set, otherwise price.unitAmount
    const effectivePrice = agreementProduct?.overwrittenUnitAmount ?? agreementProduct?.price?.unitAmount;
    if (!effectivePrice?.amount) return undefined;

    let total = effectiveArea * effectivePrice.amount;

    // For monthly recurring products, multiply by 12 to get yearly amount
    if (agreementProduct?.price?.recurring && agreementProduct?.price?.interval?.type === 'Month') {
      total = total * 12;
    }

    // Format the total
    const formatted = new Intl.NumberFormat('nb-NO', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(total);

    // Add yearly suffix if recurring, otherwise show as Engangs
    const suffix = agreementProduct?.price?.recurring ? ' kr / år' : ' kr Engangs';
    return `${formatted}${suffix}`;
  }, [agreementProduct, totalArea]);

  const formatMonthlyPrice = (amount?: number) => {
    if (amount == null) return '-';
    return (
      new Intl.NumberFormat('nb-NO', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount) + ' kr'
    );
  };

  return (
    <>
      <TableRow verticalAlign="middle">
        {/* Produktnavn */}
        <TableCell loading={isFetching}>
          <div className="flex items-center gap-2">
            {hasSubProducts && (
              <button
                type="button"
                className="p-0.5 text-muted hover:text-white transition-colors"
                onClick={onToggleExpanded}
              >
                {expanded ? <FaChevronDown className="w-3 h-3" /> : <FaChevronRight className="w-3 h-3" />}
              </button>
            )}
            <Text as="div" isEmpty={!isDefinedString(productName)}>
              <strong>{productName}</strong>
            </Text>
          </div>
        </TableCell>

        {/* Areal (from Utleieobjekt) */}
        <TableCell loading={isFetching}>
          <Text as="div" isEmpty={!isDefinedString(areaString)}>
            {areaString}
          </Text>
        </TableCell>

        {/* Overstyrt areal */}
        <TableCell loading={isFetching}>
          <Text as="div" isEmpty={!isDefinedString(overrideAreaString)}>
            {overrideAreaString || '-'}
          </Text>
        </TableCell>

        {/* Produktpris */}
        <TableCell loading={isFetching}>
          <Text as="div" isEmpty={!isDefinedString(productPrice)}>
            {productPrice}
          </Text>
        </TableCell>

        {/* Overstyrt produktpris */}
        <TableCell loading={isFetching}>
          <Text as="div" isEmpty={!isDefinedString(overwrittenPrice)}>
            {overwrittenPrice || '-'}
          </Text>
        </TableCell>

        {/* Fakturert */}
        <TableCell loading={isFetching}>
          {agreementProduct?.oneTimeTotalAmount != null ? (
            <Text as="div">{agreementProduct.isInvoiced ? tGlobal('yes') : tGlobal('no')}</Text>
          ) : (
            <Text as="div">-</Text>
          )}
        </TableCell>

        {/* Totalt */}
        <TableCell align="right" loading={isFetching}>
          <Text as="div" isEmpty={!isDefinedString(totalAmountString)}>
            <strong>{totalAmountString}</strong>
          </Text>
        </TableCell>
      </TableRow>

      {/* Sub-product rows */}
      {expanded &&
        hasSubProducts &&
        subProducts?.map((sub, index) => {
          // Find persisted child override for this sub-product
          const persisted = persistedChildren.find((c) => c.price?.product?.id === sub.id);
          const overriddenAmount = persisted?.overwrittenUnitAmount;

          return (
            <TableRow key={sub.id} verticalAlign="middle" className="bg-transparent">
              {/* Sub-product name with tree connector */}
              <TableCell>
                <div className="flex items-stretch ml-1.5">
                  <div className="flex flex-col items-center w-5 mr-2">
                    {/* Vertical line */}
                    <div
                      className={`w-px flex-1 ${index < subProducts.length - 1 ? 'bg-border' : 'bg-transparent'}`}
                      style={{ minHeight: '50%' }}
                    />
                    {/* L-connector: vertical stub + horizontal */}
                    <div className="flex items-end w-full" style={{ minHeight: '50%' }}>
                      <div className="w-px h-full bg-border" />
                      <div className="h-px flex-1 bg-border" />
                    </div>
                  </div>
                  <Text as="div" className="text-muted text-xs">
                    {sub.name}
                  </Text>
                </div>
              </TableCell>

              {/* Empty area cells */}
              <TableCell />
              <TableCell />

              {/* Sub-product monthly price */}
              <TableCell>
                <Text as="div" className="text-muted text-xs">
                  {formatMonthlyPrice(sub.unitAmount?.amount)}
                </Text>
              </TableCell>

              {/* Overridden price (if persisted) */}
              <TableCell>
                {overriddenAmount && (
                  <Text as="div" className="text-muted text-xs">
                    {formatMonthlyPrice(overriddenAmount.amount)}
                  </Text>
                )}
              </TableCell>

              {/* Empty Fakturert cell */}
              <TableCell />

              {/* Empty total cell */}
              <TableCell />
            </TableRow>
          );
        })}
    </>
  );
}
